import type { ComboboxRootChangeEventDetails } from '@base-ui/react/combobox'
import { useCallback, useState } from 'react'

import type { ComboboxModeProps, SharedProps, SingleProps } from './types'

export type SingleComboboxStateProps<T> = SharedProps<T> &
  ComboboxModeProps<T> &
  Omit<SingleProps<T>, 'multiple'>

const USER_INPUT_REASONS: ComboboxRootChangeEventDetails['reason'][] = [
  'input-change',
  'input-clear',
]

export function useSingleComboboxState<T>(props: SingleComboboxStateProps<T>) {
  const {
    options,
    getOptionLabel,
    getOptionValue: getOptionValueProp,
    onInputValueChange,
    value: singleValue = null,
    onSelect,
    isClearable = false,
  } = props

  const getOptionValue = getOptionValueProp ?? getOptionLabel

  // Base UI's input is uncontrolled — track only whether it has any text so
  // we know whether to show the clear button.
  const [hasInput, setHasInput] = useState(
    () => !!singleValue && getOptionLabel(singleValue).length > 0,
  )

  const handleInputChange = useCallback(
    (val: string, { reason }: ComboboxRootChangeEventDetails) => {
      setHasInput(val.length > 0)
      if (!USER_INPUT_REASONS.includes(reason)) return
      onInputValueChange?.(val)
    },
    [onInputValueChange],
  )

  const handleValueChange = useCallback(
    (option: T | null) => onSelect?.(option ?? null),
    [onSelect],
  )

  const handleClear = useCallback(() => {
    setHasInput(false)
    onInputValueChange?.('')
    onSelect?.(null)
  }, [onSelect, onInputValueChange])

  return {
    items: options,
    hasInput,
    isClearable,
    singleValue,
    getOptionValue,
    handleInputChange,
    handleValueChange,
    handleClear,
  }
}
